import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { Plus, Pencil, Trash2, FileSpreadsheet, FileDown, LayoutList, CalendarDays, ChevronLeft, ChevronRight, Upload, FileText, Sparkles, Download, MoveRight } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError, exportUrl, API } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  STATI_GARA, CATEGORIE_GARA, DOC_CHECKLIST, CATEGORIE_Z1, statoGaraColor, deadlineStyle, deadlineLabel,
  giorniRimanenti, fmtData, fmtDataOra, fmtEuro,
} from "../lib/helpers";
import { Popover, PopoverContent, PopoverTrigger } from "../components/ui/popover";
import {
  startOfMonth, endOfMonth, startOfWeek, endOfWeek, eachDayOfInterval, addMonths, format, isSameMonth, isToday, parseISO,
} from "date-fns";
import { it } from "date-fns/locale";

const EMPTY_FORM = {
  denominazione: "", ente_banditore: "", cig: "", cup: "", categoria: "progettazione",
  tipologia_affidamento: "", importo_base_asta: "", categorie_prestazioni: "", data_pubblicazione: "", scadenza_offerta: "",
  stato: "da_valutare", documenti: {}, referente_id: "", requisiti: [], bando_nome: "", note: "",
};

function GaraForm({ open, onClose, onSaved, gara, dipendenti }) {
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [analisi, setAnalisi] = useState(false);

  useEffect(() => {
    if (open) {
      setForm(gara
        ? { ...EMPTY_FORM, ...gara, importo_base_asta: gara.importo_base_asta ?? "", referente_id: gara.referente_id || "" }
        : EMPTY_FORM);
    }
  }, [open, gara]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const selPrestazioni = form.categorie_prestazioni ? form.categorie_prestazioni.split(",").map((s) => s.trim()).filter(Boolean) : [];
  const togglePrest = (code) => {
    const cur = new Set(selPrestazioni);
    if (cur.has(code)) cur.delete(code); else cur.add(code);
    set("categorie_prestazioni", Array.from(cur).sort().join(", "));
  };

  const analizzaBando = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setAnalisi(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/gare/parse-bando", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const x = data.estratto || {};
      setForm((prev) => ({
        ...prev,
        denominazione: x.denominazione || prev.denominazione,
        ente_banditore: x.ente_banditore || prev.ente_banditore,
        cig: x.cig || prev.cig,
        cup: x.cup || prev.cup,
        categoria: x.categoria || prev.categoria,
        tipologia_affidamento: x.tipologia_affidamento || prev.tipologia_affidamento,
        importo_base_asta: x.importo_base_asta ?? prev.importo_base_asta,
        categorie_prestazioni: x.categorie_prestazioni || prev.categorie_prestazioni,
        data_pubblicazione: (x.data_pubblicazione || "").slice(0, 10) || prev.data_pubblicazione,
        scadenza_offerta: (x.scadenza_offerta || "").slice(0, 16) || prev.scadenza_offerta,
        requisiti: x.requisiti || prev.requisiti,
        bando_nome: data.bando_nome || prev.bando_nome,
        note: prev.note || x.sintesi || "",
      }));
      toast.success("Bando analizzato: campi precompilati. Verifica prima di salvare.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setAnalisi(false);
      e.target.value = "";
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      ...form,
      importo_base_asta: form.importo_base_asta === "" ? null : Number(form.importo_base_asta),
      referente_id: form.referente_id || null,
      data_pubblicazione: form.data_pubblicazione || null,
      scadenza_offerta: form.scadenza_offerta || null,
    };
    try {
      if (gara) await api.put(`/gare/${gara.id}`, payload);
      else await api.post("/gare", payload);
      toast.success(gara ? "Gara aggiornata" : "Gara creata");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30";
  const labelCls = "block text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1";

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="gara-form-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">{gara ? "Modifica gara" : "Nuova gara"}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-2 gap-4">
          <div className="col-span-2 flex items-center justify-between gap-3 bg-blue-50 border border-blue-200 rounded-md px-4 py-3">
            <div className="text-xs text-blue-900">
              <span className="font-semibold">Analisi automatica del bando.</span> Carica il PDF: importi, requisiti economici e dati della gara verranno precompilati.
            </div>
            <label className={`flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md cursor-pointer shrink-0 ${analisi ? "bg-slate-300 text-slate-500" : "bg-blue-900 text-white hover:bg-blue-950"}`}>
              <Upload className="h-4 w-4" />
              {analisi ? "Analisi in corso…" : "Carica bando (PDF)"}
              <input type="file" accept="application/pdf" className="hidden" disabled={analisi} onChange={analizzaBando} data-testid="gara-upload-bando" />
            </label>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Denominazione / oggetto *</label>
            <input data-testid="gara-form-denominazione" required value={form.denominazione} onChange={(e) => set("denominazione", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Ente banditore</label>
            <input data-testid="gara-form-ente" value={form.ente_banditore} onChange={(e) => set("ente_banditore", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Categoria / tipologia</label>
            <Select value={form.categoria} onValueChange={(v) => set("categoria", v)}>
              <SelectTrigger data-testid="gara-form-categoria"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(CATEGORIE_GARA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>CIG</label>
            <input data-testid="gara-form-cig" value={form.cig} onChange={(e) => set("cig", e.target.value)} className={`${inputCls} font-code`} />
          </div>
          <div>
            <label className={labelCls}>CUP</label>
            <input data-testid="gara-form-cup" value={form.cup} onChange={(e) => set("cup", e.target.value)} className={`${inputCls} font-code`} />
          </div>
          <div>
            <label className={labelCls}>Importo a base d'asta (€)</label>
            <input data-testid="gara-form-importo" type="number" step="0.01" min="0" value={form.importo_base_asta} onChange={(e) => set("importo_base_asta", e.target.value)} className={`${inputCls} font-code`} />
          </div>
          <div>
            <label className={labelCls}>Tipologia di affidamento</label>
            <input data-testid="gara-form-affidamento" value={form.tipologia_affidamento} onChange={(e) => set("tipologia_affidamento", e.target.value)} className={inputCls} placeholder="es. procedura aperta, affidamento diretto" />
          </div>
          <div>
            <label className={labelCls}>Categorie prestazioni (Tavola Z-1 D.M. 17/06/2016)</label>
            <Popover>
              <PopoverTrigger asChild>
                <button type="button" data-testid="gara-form-prestazioni" className={`${inputCls} text-left ${selPrestazioni.length ? "text-slate-800" : "text-slate-400"}`}>
                  {selPrestazioni.length ? selPrestazioni.join(", ") : "Seleziona categorie…"}
                </button>
              </PopoverTrigger>
              <PopoverContent className="w-96 max-h-72 overflow-y-auto p-2" align="start">
                {CATEGORIE_Z1.map((gr) => (
                  <div key={gr.gruppo} className="mb-1.5">
                    <div className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider text-slate-400 sticky top-0 bg-white">{gr.gruppo}</div>
                    {gr.voci.map(([code, desc]) => (
                      <label key={code} data-testid={`gara-prest-${code.replace(".", "-")}`} className="flex items-start gap-2 px-2 py-1 rounded hover:bg-slate-50 cursor-pointer">
                        <input type="checkbox" checked={selPrestazioni.includes(code)} onChange={() => togglePrest(code)} className="h-3.5 w-3.5 mt-0.5 accent-blue-900" />
                        <span className="text-xs leading-snug"><span className="font-code font-semibold text-slate-700">{code}</span> <span className="text-slate-500">{desc}</span></span>
                      </label>
                    ))}
                  </div>
                ))}
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <label className={labelCls}>Data pubblicazione bando</label>
            <input data-testid="gara-form-pubblicazione" type="date" value={form.data_pubblicazione || ""} onChange={(e) => set("data_pubblicazione", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Scadenza presentazione offerta</label>
            <input data-testid="gara-form-scadenza" type="datetime-local" value={form.scadenza_offerta || ""} onChange={(e) => set("scadenza_offerta", e.target.value)} className={inputCls} />
          </div>
          <div>
            <label className={labelCls}>Stato</label>
            <Select value={form.stato} onValueChange={(v) => set("stato", v)}>
              <SelectTrigger data-testid="gara-form-stato"><SelectValue /></SelectTrigger>
              <SelectContent>
                {Object.entries(STATI_GARA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className={labelCls}>Referente interno</label>
            <Select value={form.referente_id} onValueChange={(v) => set("referente_id", v === "__none" ? "" : v)}>
              <SelectTrigger data-testid="gara-form-referente"><SelectValue placeholder="—" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="__none">—</SelectItem>
                {dipendenti.map((d) => <SelectItem key={d.id} value={d.id}>{d.nome} {d.cognome}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2">
            <label className={labelCls}>Checklist documenti richiesti</label>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 border border-slate-200 rounded-md p-3">
              {DOC_CHECKLIST.map((doc) => (
                <label key={doc} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer">
                  <input
                    type="checkbox"
                    data-testid={`gara-doc-${doc.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    checked={!!form.documenti[doc]}
                    onChange={(e) => set("documenti", { ...form.documenti, [doc]: e.target.checked })}
                    className="h-3.5 w-3.5 accent-blue-900"
                  />
                  {doc}
                </label>
              ))}
            </div>
          </div>
          {(form.requisiti || []).length > 0 && (
            <div className="col-span-2" data-testid="gara-requisiti-table">
              <label className={labelCls}>Requisiti per categoria di progettazione estratti dal bando</label>
              <div className="border border-slate-200 rounded-md overflow-hidden">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200 text-left">
                      <th className="px-2 py-1.5 font-semibold text-slate-500 w-24">Categoria</th>
                      <th className="px-2 py-1.5 font-semibold text-slate-500">Requisito</th>
                      <th className="px-2 py-1.5 font-semibold text-slate-500 w-28">Importo req.</th>
                      <th className="px-2 py-1.5 font-semibold text-slate-500 w-28">Importo lavori</th>
                      <th className="w-8"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {form.requisiti.map((r, i) => (
                      <tr key={i} className="border-b border-slate-100 last:border-0" data-testid={`gara-requisito-${i}`}>
                        <td className="px-2 py-1.5 font-code font-semibold text-slate-700">{r.categoria}</td>
                        <td className="px-2 py-1.5 text-slate-600">{r.requisito}</td>
                        <td className="px-2 py-1.5 font-code text-slate-700">{r.importo != null ? fmtEuro(r.importo) : "—"}</td>
                        <td className="px-2 py-1.5 font-code text-slate-700">{r.importo_lavori != null ? fmtEuro(r.importo_lavori) : "—"}</td>
                        <td className="px-1 text-right">
                          <button type="button" data-testid={`gara-requisito-del-${i}`} onClick={() => set("requisiti", form.requisiti.filter((_, j) => j !== i))} className="p-1 rounded hover:bg-red-100 text-slate-400 hover:text-red-700">
                            <Trash2 className="h-3 w-3" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
          <div className="col-span-2">
            <label className={labelCls}>Note / allegati (riferimenti)</label>
            <textarea data-testid="gara-form-note" rows={2} value={form.note} onChange={(e) => set("note", e.target.value)} className={inputCls} placeholder="Riferimenti a bando, disciplinare, capitolato (cartella condivisa, link, ecc.)" />
          </div>
          <div className="col-span-2 flex justify-end gap-2 pt-2">
            <button type="button" onClick={onClose} data-testid="gara-form-cancel" className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50">Annulla</button>
            <button type="submit" disabled={saving} data-testid="gara-form-submit" className="px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
              {saving ? "Salvataggio…" : "Salva gara"}
            </button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportDialog({ open, onClose, onDone }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [risultato, setRisultato] = useState(null);

  useEffect(() => {
    if (open) { setFile(null); setRisultato(null); }
  }, [open]);

  const submit = async () => {
    if (!file) return;
    setLoading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/gare/import-file", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setRisultato(data);
      onDone();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display">Importa gare da MyTelemat</DialogTitle>
        </DialogHeader>
        <p className="text-xs text-slate-500">
          Esporta l'elenco gare dall'area riservata MyTelemat in formato <strong>XLS o CSV</strong> e caricalo qui.
          Le gare vengono create come "Da valutare", con deduplica automatica su CIG/oggetto e filtri configurabili (Impostazioni).
        </p>
        <input type="file" accept=".csv,.xlsx,.xls" data-testid="gare-import-file" onChange={(e) => setFile(e.target.files?.[0] || null)}
          className="text-sm file:mr-3 file:px-3 file:py-2 file:rounded-md file:border-0 file:bg-blue-900 file:text-white file:text-xs file:font-semibold hover:file:bg-blue-950" />
        {risultato && (
          <div className="bg-green-50 border border-green-200 rounded-md px-4 py-3 text-sm" data-testid="gare-import-result">
            <div className="font-semibold text-green-800">Importazione completata</div>
            <div className="text-green-700 text-xs mt-1 font-code">
              {risultato.create} gare create · {risultato.duplicate_saltate} duplicate saltate · {risultato.filtrate} escluse dai filtri · {risultato.righe_lette} righe lette
            </div>
            {(risultato.esempi_duplicati || []).length > 0 && (
              <div className="text-[11px] text-slate-500 mt-1">Duplicati: {risultato.esempi_duplicati.join(" · ")}</div>
            )}
          </div>
        )}
        <div className="flex justify-end gap-2">
          <button onClick={onClose} data-testid="gare-import-close" className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md hover:bg-slate-50">Chiudi</button>
          <button onClick={submit} disabled={!file || loading} data-testid="gare-import-submit"
            className="px-4 py-2 text-sm font-semibold bg-blue-900 text-white rounded-md hover:bg-blue-950 disabled:opacity-60">
            {loading ? "Importazione…" : "Importa gare"}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CalendarioGare({ gare }) {
  const [mese, setMese] = useState(new Date());
  const giorni = useMemo(() => {
    const start = startOfWeek(startOfMonth(mese), { weekStartsOn: 1 });
    const end = endOfWeek(endOfMonth(mese), { weekStartsOn: 1 });
    return eachDayOfInterval({ start, end });
  }, [mese]);

  const perGiorno = useMemo(() => {
    const map = {};
    gare.forEach((g) => {
      if (!g.scadenza_offerta) return;
      const key = g.scadenza_offerta.slice(0, 10);
      (map[key] = map[key] || []).push(g);
    });
    return map;
  }, [gare]);

  return (
    <div className="bg-white border border-slate-200 rounded-md" data-testid="gare-calendar">
      <div className="flex items-center justify-between px-4 py-3 border-b border-slate-200">
        <button data-testid="cal-prev-month" onClick={() => setMese(addMonths(mese, -1))} className="p-1.5 rounded hover:bg-slate-100"><ChevronLeft className="h-4 w-4" /></button>
        <div className="font-display font-semibold text-slate-800 capitalize">{format(mese, "MMMM yyyy", { locale: it })}</div>
        <button data-testid="cal-next-month" onClick={() => setMese(addMonths(mese, 1))} className="p-1.5 rounded hover:bg-slate-100"><ChevronRight className="h-4 w-4" /></button>
      </div>
      <div className="grid grid-cols-7 text-center text-[11px] font-semibold uppercase tracking-wider text-slate-400 border-b border-slate-200">
        {["Lun", "Mar", "Mer", "Gio", "Ven", "Sab", "Dom"].map((d) => <div key={d} className="py-2">{d}</div>)}
      </div>
      <div className="grid grid-cols-7">
        {giorni.map((day) => {
          const key = format(day, "yyyy-MM-dd");
          const items = perGiorno[key] || [];
          return (
            <div key={key} className={`min-h-24 border-b border-r border-slate-100 p-1.5 ${!isSameMonth(day, mese) ? "bg-slate-50/60" : ""}`}>
              <div className={`text-xs font-code mb-1 ${isToday(day) ? "h-5 w-5 rounded-full bg-blue-900 text-white flex items-center justify-center" : "text-slate-500"}`}>
                {format(day, "d")}
              </div>
              <div className="space-y-1">
                {items.slice(0, 3).map((g) => {
                  const gg = giorniRimanenti(g.scadenza_offerta);
                  const st = deadlineStyle(gg);
                  return (
                    <div key={g.id} data-testid={`cal-gara-${g.id}`} title={g.denominazione}
                      className={`text-[10px] leading-tight px-1 py-0.5 rounded border truncate ${st.badge}`}>
                      {g.denominazione}
                    </div>
                  );
                })}
                {items.length > 3 && <div className="text-[10px] text-slate-400">+{items.length - 3} altre</div>}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function Gare() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [gare, setGare] = useState([]);
  const [dipendenti, setDipendenti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtroStato, setFiltroStato] = useState("tutte");
  const [filtroScadenza, setFiltroScadenza] = useState("tutte");
  const [filtroEnte, setFiltroEnte] = useState("");
  const [soloMie, setSoloMie] = useState(false);
  const [filtroCatTlm, setFiltroCatTlm] = useState("tutte");
  const [filtroRegione, setFiltroRegione] = useState("tutte");
  const [importoMin, setImportoMin] = useState("");
  const [importoMax, setImportoMax] = useState("");
  const [vista, setVista] = useState("tabella");
  const [importOpen, setImportOpen] = useState(false);
  const [syncLoading, setSyncLoading] = useState(false);
  const [analyzingId, setAnalyzingId] = useState(null);

  const syncTelemat = async () => {
    setSyncLoading(true);
    try {
      const { data } = await api.post("/telemat/sync");
      if (data.errore) toast.error(data.errore);
      else toast.success(`Sync Telemat: ${data.create} nuove gare · ${data.duplicate} già presenti · ${data.filtrate} filtrate`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSyncLoading(false);
    }
  };

  const scaricaBando = async (g) => {
    setAnalyzingId(g.id);
    try {
      const { data } = await api.post(`/gare/${g.id}/scarica-bando`);
      if (data.analisi_ok) toast.success(`Bando "${data.documento_scaricato}" scaricato e analizzato: campi e requisiti aggiornati`);
      else toast.success(`Bando "${data.documento_scaricato}" scaricato (PDF senza testo estraibile — dati non analizzati)`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setAnalyzingId(null);
    }
  };
  const [sezione, setSezione] = useState("in_corso");

  const trasferisci = async (g) => {
    try {
      await api.post(`/gare/${g.id}/trasferisci-in-corso`);
      toast.success("Gara spostata in \"Gare in corso\"");
      setSezione("in_corso");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const q = searchParams.get("q") || "";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [g, d] = await Promise.all([api.get("/gare"), api.get("/dipendenti/lookup")]);
      setGare(g.data);
      setDipendenti(d.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (searchParams.get("nuova") === "1") {
      setEditing(null);
      setDialogOpen(true);
      searchParams.delete("nuova");
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, isAdmin, setSearchParams]);

  const filtrate = useMemo(() => {
    return gare.filter((g) => {
      if (soloMie && user?.dipendente_id && g.referente_id !== user.dipendente_id) return false;
      if (filtroStato !== "tutte" && g.stato !== filtroStato) return false;
      const gg = giorniRimanenti(g.scadenza_offerta);
      if (filtroScadenza === "7" && !(gg !== null && gg >= 0 && gg <= 7)) return false;
      if (filtroScadenza === "15" && !(gg !== null && gg >= 0 && gg <= 15)) return false;
      if (filtroScadenza === "30" && !(gg !== null && gg >= 0 && gg <= 30)) return false;
      if (filtroEnte && !(g.ente_banditore || "").toLowerCase().includes(filtroEnte.toLowerCase())) return false;
      if (filtroCatTlm !== "tutte" && !(g.categorie_telemat || "").split(",").map((s) => s.trim()).includes(filtroCatTlm)) return false;
      if (filtroRegione !== "tutte" && (g.regione || "") !== filtroRegione) return false;
      if (importoMin !== "" && !(g.importo_base_asta != null && g.importo_base_asta >= Number(importoMin))) return false;
      if (importoMax !== "" && !(g.importo_base_asta != null && g.importo_base_asta <= Number(importoMax))) return false;
      if (q && !(g.denominazione || "").toLowerCase().includes(q.toLowerCase())) return false;
      return true;
    });
  }, [gare, filtroStato, filtroScadenza, filtroEnte, soloMie, filtroCatTlm, filtroRegione, importoMin, importoMax, q]);

  const catTelematList = useMemo(() => {
    const setC = new Set();
    gare.forEach((g) => (g.categorie_telemat || "").split(",").map((s) => s.trim()).filter(Boolean).forEach((x) => setC.add(x)));
    return Array.from(setC).sort();
  }, [gare]);

  const regioniList = useMemo(
    () => Array.from(new Set(gare.map((g) => g.regione).filter(Boolean))).sort(),
    [gare]
  );

  const storico = filtrate.filter((g) => ["aggiudicata", "persa", "esclusa"].includes(g.stato));
  const attive = filtrate.filter((g) => !["aggiudicata", "persa", "esclusa"].includes(g.stato));
  const inCorso = attive.filter((g) => g.fonte !== "telemat" || g.in_corso);
  const pubblicate = attive.filter((g) => g.fonte === "telemat" && !g.in_corso);

  const elimina = async (g) => {
    if (!window.confirm(`Eliminare la gara "${g.denominazione}"?`)) return;
    try {
      await api.delete(`/gare/${g.id}`);
      toast.success("Gara eliminata");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const refNome = (id) => {
    const d = dipendenti.find((x) => x.id === id);
    return d ? `${d.nome} ${d.cognome}` : "—";
  };

  const docFatti = (docs) => DOC_CHECKLIST.filter((k) => docs?.[k]).length;

  const renderTable = (items, testidPrefix, isStorico = false) => (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden">
      <table className="w-full text-sm" data-testid={testidPrefix}>
        <thead>
          <tr className="border-b border-slate-200 bg-slate-50 text-left">
            {["Gara", "Ente", "CIG/CUP", "Importo", "Scadenza offerta", "Stato", "Documenti", "Referente", ""].map((h, i) => (
              <th key={i} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wider text-slate-500">{h}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {items.length === 0 && (
            <tr><td colSpan={9} className="px-4 py-8 text-center text-slate-500">Nessuna gara trovata.</td></tr>
          )}
          {items.map((g) => {
            const gg = giorniRimanenti(g.scadenza_offerta);
            const st = isStorico ? deadlineStyle(null) : deadlineStyle(gg);
            return (
              <tr key={g.id} data-testid={`gara-row-${g.id}`} className={`border-b border-slate-100 last:border-0 hover:bg-slate-50/70 ${st.row}`}>
                <td className="px-3 py-2.5 max-w-64">
                  <div className="font-medium text-slate-800 truncate">{g.denominazione}</div>
                  <div className="text-xs text-slate-500">
                    {CATEGORIE_GARA[g.categoria] || g.categoria}
                    {g.fonte === "telemat" && <Badge variant="outline" className="ml-1.5 bg-sky-50 text-sky-700 border-sky-200 text-[10px]">Telemat</Badge>}
                  </div>
                </td>
                <td className="px-3 py-2.5 text-slate-600 max-w-40 truncate">{g.ente_banditore || "—"}</td>
                <td className="px-3 py-2.5 font-code text-xs text-slate-600">{g.cig || "—"}{g.cup ? ` / ${g.cup}` : ""}</td>
                <td className="px-3 py-2.5 font-code text-xs text-slate-700 whitespace-nowrap">{fmtEuro(g.importo_base_asta)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap">
                  <div className="text-xs text-slate-700">{fmtDataOra(g.scadenza_offerta)}</div>
                  {!isStorico && gg !== null && <Badge variant="outline" className={`${st.badge} font-code mt-0.5`}>{deadlineLabel(gg)}</Badge>}
                </td>
                <td className="px-3 py-2.5">
                  <Badge variant="outline" className={statoGaraColor[g.stato]}>{STATI_GARA[g.stato]}</Badge>
                </td>
                <td className="px-3 py-2.5 text-xs text-slate-600 font-code">{docFatti(g.documenti)}/{DOC_CHECKLIST.length}</td>
                <td className="px-3 py-2.5 text-xs text-slate-600 whitespace-nowrap">{refNome(g.referente_id)}</td>
                <td className="px-3 py-2.5 whitespace-nowrap text-right">
                  {!isStorico && g.fonte === "telemat" && !g.in_corso && (
                    <button data-testid={`gara-trasferisci-${g.id}`} title="Sposta in Gare in corso" onClick={() => trasferisci(g)}
                      className="inline-block p-1.5 rounded hover:bg-blue-100 text-blue-800 align-middle">
                      <MoveRight className="h-3.5 w-3.5" />
                    </button>
                  )}
                  {g.fonte === "telemat" && (
                    <button data-testid={`gara-scarica-bando-${g.id}`} title="Scarica bando da MyTelemat e analizza" disabled={analyzingId === g.id}
                      onClick={() => scaricaBando(g)}
                      className="inline-block p-1.5 rounded hover:bg-sky-100 text-sky-700 align-middle disabled:opacity-50">
                      <Sparkles className={`h-3.5 w-3.5 ${analyzingId === g.id ? "animate-pulse" : ""}`} />
                    </button>
                  )}
                  {g.bando_disponibile && (
                    <a href={`${API}/gare/${g.id}/bando`} data-testid={`gara-download-bando-${g.id}`} title="Scarica il PDF del bando"
                      className="inline-block p-1.5 rounded hover:bg-emerald-100 text-emerald-700 align-middle">
                      <Download className="h-3.5 w-3.5" />
                    </a>
                  )}
                  <a href={`${API}/gare/${g.id}/report`} data-testid={`gara-report-${g.id}`} title="Report PDF della gara"
                    className="inline-block p-1.5 rounded hover:bg-slate-200 text-slate-500 align-middle">
                    <FileText className="h-3.5 w-3.5" />
                  </a>
                  {(isAdmin || g.referente_id === user?.dipendente_id) && (
                    <button data-testid={`gara-edit-${g.id}`} onClick={() => { setEditing(g); setDialogOpen(true); }} className="p-1.5 rounded hover:bg-slate-200 text-slate-500"><Pencil className="h-3.5 w-3.5" /></button>
                  )}
                  {isAdmin && (
                    <button data-testid={`gara-delete-${g.id}`} onClick={() => elimina(g)} className="p-1.5 rounded hover:bg-red-100 text-slate-500 hover:text-red-700"><Trash2 className="h-3.5 w-3.5" /></button>
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );

  return (
    <div className="space-y-5" data-testid="gare-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="font-display text-2xl lg:text-3xl font-bold tracking-tight text-slate-900">Gare d'appalto</h1>
          <p className="text-sm text-slate-500 mt-1">{inCorso.length} in corso · {pubblicate.length} pubblicate da Telemat · {storico.length} concluse nello storico</p>
        </div>
        <div className="flex items-center gap-2">
          <a href={exportUrl("gare", "excel")} data-testid="export-excel-button"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
            <FileSpreadsheet className="h-4 w-4" /> Excel
          </a>
          <a href={exportUrl("gare", "pdf")} data-testid="export-pdf-button"
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50">
            <FileDown className="h-4 w-4" /> PDF
          </a>
          <button data-testid="gare-import-trigger" onClick={() => setImportOpen(true)}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md border border-blue-900 text-blue-900 hover:bg-blue-50">
            <Upload className="h-4 w-4" /> Importa MyTelemat
          </button>
          <button data-testid="gare-sync-telemat" onClick={syncTelemat} disabled={syncLoading}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-sky-700 text-white hover:bg-sky-800 disabled:opacity-60">
            <Upload className="h-4 w-4" /> {syncLoading ? "Sincronizzo…" : "Sincronizza Telemat"}
          </button>
          <div className="flex border border-slate-300 rounded-md overflow-hidden">
            <button data-testid="gare-view-table" onClick={() => setVista("tabella")}
              className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${vista === "tabella" ? "bg-blue-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <LayoutList className="h-4 w-4" /> Elenco
            </button>
            <button data-testid="gare-view-calendar" onClick={() => setVista("calendario")}
              className={`px-3 py-2 text-xs font-semibold flex items-center gap-1.5 ${vista === "calendario" ? "bg-blue-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
              <CalendarDays className="h-4 w-4" /> Calendario
            </button>
          </div>
          <button data-testid="gare-add-modal-trigger" onClick={() => { setEditing(null); setDialogOpen(true); }}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-semibold rounded-md bg-blue-900 text-white hover:bg-blue-950">
            <Plus className="h-4 w-4" /> Nuova gara
          </button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap bg-white border border-slate-200 rounded-md px-4 py-3">
        <Select value={filtroStato} onValueChange={setFiltroStato}>
          <SelectTrigger data-testid="gare-filter-status-select" className="w-48 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutti gli stati</SelectItem>
            {Object.entries(STATI_GARA).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroScadenza} onValueChange={setFiltroScadenza}>
          <SelectTrigger data-testid="gare-filter-deadline-select" className="w-48 h-9 text-sm"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le scadenze</SelectItem>
            <SelectItem value="7">Entro 7 giorni</SelectItem>
            <SelectItem value="15">Entro 15 giorni</SelectItem>
            <SelectItem value="30">Entro 30 giorni</SelectItem>
          </SelectContent>
        </Select>
        <input
          data-testid="gare-filter-ente-input"
          value={filtroEnte}
          onChange={(e) => setFiltroEnte(e.target.value)}
          placeholder="Filtra per ente…"
          className="w-56 px-3 py-2 text-sm bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30 h-9"
        />
        {(vista === "calendario" || sezione !== "in_corso") && (
          <>
        <Select value={filtroCatTlm} onValueChange={setFiltroCatTlm}>
          <SelectTrigger data-testid="gare-filter-categoria-telemat" className="w-44 h-9 text-sm"><SelectValue placeholder="Categoria SOA" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le categorie SOA</SelectItem>
            {catTelematList.map((c) => <SelectItem key={c} value={c}><span className="font-code">{c}</span></SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filtroRegione} onValueChange={setFiltroRegione}>
          <SelectTrigger data-testid="gare-filter-regione" className="w-40 h-9 text-sm"><SelectValue placeholder="Regione" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tutte">Tutte le regioni</SelectItem>
            {regioniList.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
          </SelectContent>
        </Select>
        <input type="number" min="0" data-testid="gare-filter-importo-min" value={importoMin} onChange={(e) => setImportoMin(e.target.value)}
          placeholder="Importo min €" className="w-32 px-3 py-2 text-sm font-code bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30 h-9" />
        <input type="number" min="0" data-testid="gare-filter-importo-max" value={importoMax} onChange={(e) => setImportoMax(e.target.value)}
          placeholder="Importo max €" className="w-32 px-3 py-2 text-sm font-code bg-white border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-900/30 h-9" />
          </>
        )}
        <label className="flex items-center gap-2 text-xs text-slate-600 cursor-pointer">
          <input type="checkbox" data-testid="gare-filter-mie" checked={soloMie} onChange={(e) => setSoloMie(e.target.checked)} className="h-3.5 w-3.5 accent-blue-900" />
          Solo le mie gare
        </label>
      </div>

      {loading ? (
        <div className="py-16 text-center text-slate-500 text-sm" data-testid="gare-loading">Caricamento gare…</div>
      ) : vista === "calendario" ? (
        <CalendarioGare gare={filtrate} />
      ) : (
        <div className="space-y-4">
          <div className="flex border border-slate-300 rounded-md overflow-hidden w-fit bg-white" data-testid="gare-sezioni-tabs">
            {[
              ["in_corso", `Gare in corso (${inCorso.length})`],
              ["pubblicate", `Gare pubblicate (${pubblicate.length})`],
              ["storico", `Storico (${storico.length})`],
            ].map(([k, label]) => (
              <button key={k} data-testid={`gare-tab-${k}`} onClick={() => setSezione(k)}
                className={`px-4 py-2 text-xs font-semibold ${sezione === k ? "bg-blue-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                {label}
              </button>
            ))}
          </div>
          {sezione === "in_corso" && (
            <section>
              <p className="text-xs text-slate-500 mb-2">Gare in lavorazione o inserite manualmente dallo studio.</p>
              {renderTable(inCorso, "gare-table")}
            </section>
          )}
          {sezione === "pubblicate" && (
            <section>
              <p className="text-xs text-slate-500 mb-2">Gare pubblicate importate da MyTelemat. Usa il pulsante → sulla riga per spostare una gara in "Gare in corso".</p>
              {renderTable(pubblicate, "gare-pubblicate-table")}
            </section>
          )}
          {sezione === "storico" && (
            <section>
              <p className="text-xs text-slate-500 mb-2">Gare concluse (aggiudicate, perse o escluse).</p>
              {renderTable(storico, "gare-storico-table", true)}
            </section>
          )}
        </div>
      )}

      <ImportDialog open={importOpen} onClose={() => setImportOpen(false)} onDone={load} />
      <GaraForm open={dialogOpen} onClose={() => setDialogOpen(false)} onSaved={load} gara={editing} dipendenti={dipendenti} />
    </div>
  );
}
